import { useMutation } from '@tanstack/react-query';

import { prepareAssets } from 'apis/asset-repository-service/api';
import { AssetScope } from 'apis/asset-repository-service/types';
import useUser from 'common/hooks/useUser';

import { useScopeContext } from '../../contexts/ScopeContext';

const isAssetScope = (scope: string): scope is AssetScope['type'] =>
  ['venue', 'merchant', 'brand', 'country', 'unspecified'].includes(scope);

const getFileDetail = (FileOrUrl: File | string) => {
  if (typeof FileOrUrl === 'object') {
    return {
      contentType: FileOrUrl.type,
      name: FileOrUrl.name,
    };
  }

  const lastUrlPart = FileOrUrl.split('/').pop() ?? '';
  const extension = lastUrlPart.split('.').pop();
  const name = lastUrlPart.split('.').slice(0, -1).join('.');
  let contentType = '*/*';

  switch (extension) {
    case 'jpeg':
    case 'jpg':
      contentType = 'image/jpeg';
      break;
    case 'png':
      contentType = 'image/png';
      break;
    case 'webp':
      contentType = 'image/webp';
      break;
  }

  return {
    contentType,
    name,
  };
};

export const useAssetRepositoryPrepareAsset = () => {
  const { isEmployee, user } = useUser();
  const scope = useScopeContext();

  const { mutateAsync: prepareAssetsMutation, isLoading } = useMutation(
    async (filesOrUrls: File[] | string[]) => {
      if (!user) {
        throw new Error('User must be authorized to upload assets');
      }

      return await prepareAssets(
        filesOrUrls.map(fileOrUrl => ({
          ...getFileDetail(fileOrUrl),
          imageResizerProxyUrl: typeof fileOrUrl === 'string' ? fileOrUrl : null,
          scope: {
            type: isAssetScope(scope.type) ? scope.type : 'unspecified',
            scopeId: scope.id,
          },
          source: { type: isEmployee ? 'wolt_employee' : 'self_service', sourceId: user.id },
        })),
      );
    },
    {
      retry: 1,
    },
  );

  return { prepareAssets: prepareAssetsMutation, isLoading };
};
